import { useCallback, useEffect, useRef, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { IconButton } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { useSegmentRulesAgent, type AgentState } from "app/pages/segments/redesigned-wizard/segment-wizard/hooks/useSegmentRulesAgent";
import { allTrackers } from "app/services/track/track";

import type { IRule } from "../types";

import {
    AiSectionContainer,
    AiSectionHeader,
    AiSectionHeaderLabel,
    OptionalBadge,
    AiSectionBody,
    DescriptionSubtitle,
    DescriptionTextArea,
    GenerateButtonRow,
    ErrorContainer,
} from "./styledComponents";

const AGENT_STATE: Record<AgentState, AgentState> = {
    idle: "idle",
    generating: "generating",
    done: "done",
    error: "error",
};

const hasNonSpecialChars = (value: string) => /[\p{L}\p{N}]/u.test(value);

type AiDescriptionSectionProps = {
    domain: string;
    country: string;
    defaultOpen?: boolean;
    onRulesGenerated: (rules: IRule[], description: string) => void;
    onGeneratingChange: (isGenerating: boolean) => void;
};

export const AiDescriptionSection = ({
    domain,
    country,
    defaultOpen = true,
    onRulesGenerated,
    onGeneratingChange,
}: AiDescriptionSectionProps) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [description, setDescription] = useState("");
    const { state, generatedRules, error, generate, reset } = useSegmentRulesAgent();
    const appliedRef = useRef(false);
    const descriptionAtGenerationRef = useRef("");

    useEffect(() => {
        onGeneratingChange(state === AGENT_STATE.generating);
    }, [state, onGeneratingChange]);

    useEffect(() => {
        if (state === AGENT_STATE.done && generatedRules.length > 0 && !appliedRef.current) {
            appliedRef.current = true;
            onRulesGenerated(generatedRules, descriptionAtGenerationRef.current);
            reset();
        }
        if (state !== AGENT_STATE.done) {
            appliedRef.current = false;
        }
    }, [state, generatedRules, onRulesGenerated, reset]);

    const handleGenerate = useCallback(() => {
        if (!description.trim() || !domain) return;
        allTrackers.dispatchTrackEvent("Button", "click", "segmentWizard/createSegmentRules", {
            domain,
            country,
            description: description.trim(),
        });
        descriptionAtGenerationRef.current = description.trim();
        generate(domain, description.trim(), country);
    }, [domain, description, country, generate]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey && description.trim().length > 0 && hasNonSpecialChars(description) && state === AGENT_STATE.idle) {
                e.preventDefault();
                handleGenerate();
            }
        },
        [description, state, handleGenerate],
    );

    const isGenerateDisabled =
        description.trim().length === 0 ||
        !hasNonSpecialChars(description) ||
        state === AGENT_STATE.generating ||
        !domain;

    return (
        <AiSectionContainer data-automation="segment-wizard-ai-section">
            <AiSectionHeader
                onClick={() => setIsOpen((prev) => !prev)}
                data-automation="segment-wizard-ai-section-toggle"
            >
                <SWReactIcons
                    iconName={isOpen ? "chev-up" : "add"}
                    size="xs"
                />
                <AiSectionHeaderLabel>
                    {i18nFilter()("segment.wizard.ai.section.title") || "Describe your segment"}
                </AiSectionHeaderLabel>
                <OptionalBadge>
                    {i18nFilter()("segment.wizard.ai.section.optional") || "OPTIONAL"}
                </OptionalBadge>
            </AiSectionHeader>

            {isOpen && (
                <AiSectionBody>
                    <DescriptionSubtitle>
                        {i18nFilter()("segment.wizard.ai.section.subtitle") ||
                            "Describe the audience you want to target and AI will generate the rules below. You can then adjust them freely."}
                    </DescriptionSubtitle>
                    <DescriptionTextArea
                        data-automation="segment-wizard-ai-description-input"
                        value={description}
                        onChange={(e) => setDescription(e.target.value.slice(0, 100))}
                        onKeyDown={handleKeyDown}
                        disabled={state === AGENT_STATE.generating}
                        maxLength={100}
                        rows={3}
                    />

                    {state === AGENT_STATE.error && error && (
                        <ErrorContainer>{error}</ErrorContainer>
                    )}

                    <GenerateButtonRow>
                        <IconButton
                            data-automation="segment-wizard-ai-generate-btn"
                            type="primary"
                            iconName="ai-search"
                            iconSize="sm"
                            isDisabled={isGenerateDisabled}
                            onClick={handleGenerate}
                        >
                            {state === AGENT_STATE.error
                                ? (i18nFilter()("segment.wizard.ai.regenerate.button") || "Try Again")
                                : (i18nFilter()("segment.wizard.ai.generate.button") || "Generate rules")}
                        </IconButton>
                    </GenerateButtonRow>
                </AiSectionBody>
            )}
        </AiSectionContainer>
    );
};
